import { useQuery } from "@tanstack/react-query";
import { erp } from "@/lib/erp";
import type { ItemPrice } from "@/lib/erp";
import type { ItemPriceListParams } from "@/lib/erp/itemPrices";
import { ITEM_PRICE_LIST_FIELDS } from "@/lib/erp/itemPrices";
import type { FilterDescriptor } from "@/lib/erp";
import type { FrappeFilterArray } from "@/lib/erp/filterMapper";
import { useDocTypeListWithPagination } from "@/features/common/api/useDocTypeListWithPagination";

/**
 * Parameters for useItemPriceListWithPagination
 *
 * Note: filterDescriptors is the preferred way to pass filters.
 * The preMappedFilters param is for legacy/advanced use only (pre-mapped Frappe filters).
 */
export type ItemPriceListHookParams = Omit<ItemPriceListParams, "filters"> & {
  /**
   * FilterDescriptor[] to be converted to Frappe format internally.
   * This is the preferred way to pass filters for normal item price use.
   */
  filterDescriptors?: FilterDescriptor[];
  /**
   * Legacy/advanced: pass pre-mapped Frappe filter array.
   * Prefer filterDescriptors in new code.
   *
   * @deprecated Use filterDescriptors instead. This is kept for backwards compatibility.
   */
  preMappedFilters?: FrappeFilterArray;
  /**
   * @deprecated Use preMappedFilters instead for clarity. Kept for backwards compatibility.
   */
  filters?: FrappeFilterArray;
  /**
   * Optional: fields to fetch from backend.
   * If not provided, defaults to ITEM_PRICE_LIST_FIELDS.
   * If provided, overrides the default fields list.
   */
  fields?: string[];
  /**
   * Search query string (will be converted to or_filters internally)
   */
  searchQuery?: string;
};

/**
 * Hook for fetching item prices with server-side pagination and filtering.
 *
 * This is a thin wrapper around the generic useDocTypeListWithPagination hook,
 * providing Item Price–specific defaults (doctype: "Item Price",
 * searchFields: ["name", "item_code", "price_list"]).
 */
export function useItemPriceListWithPagination(
  params: ItemPriceListHookParams
) {
  // Support both preMappedFilters (preferred) and filters (deprecated) for backwards compatibility
  // Destructure fields separately to prevent it from being overwritten by spread operator
  const { preMappedFilters, filters, fields, ...restParams } = params;
  const legacyFilters = preMappedFilters || filters;

  return useDocTypeListWithPagination<ItemPrice>({
    doctype: "Item Price",
    searchFields: ["name", "item_code", "price_list"],
    fields: fields ?? [...ITEM_PRICE_LIST_FIELDS], // Use destructured fields or default
    listMethodOverride: "zaviago_backend.api.doc_list.list_with_system_fields", // Use POST method to avoid URL encoding issues with LIKE patterns
    ...restParams, // Now excludes fields, so it won't overwrite
    // Map to the generic hook's parameter name
    filters: legacyFilters,
  });
}

export function useItemPrice(name: string) {
  return useQuery({
    queryKey: ["item-prices", "detail", name],
    queryFn: () => erp.itemPrices.detail(name),
    enabled: !!name,
  });
}

